import {
  Button,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Stack,
  Typography,
} from '@mui/material'
import moment from 'moment'
import React from 'react'
import { AccessTime, Place } from '@mui/icons-material'
import ItemCard from './shared/ItemCard'
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { numberFormat } from '@/helpers/numbers'

const canteenOrderedItemCardUserDataFragment = graphql(`
  fragment CanteenOrderedItemCard_UserDataFragment on User {
    account {
      userInterfaceSettings {
        hideCCPrices
      }
    }
  }
`)

const canteenOrderedItemCardOrderItemFragment = graphql(`
  fragment CanteenOrderedItemCard_OrderItemFragment on CCOrderItem {
    id
    name
    qty
    unitPrice
    ccSupplierOrder {
      arrivalTime
    }
    vendor {
      companyName
      logo
    }
    accountLocation {
      id
      label
    }
    canCancel
    liveMenuItem {
      coreLiveItem {
        image
      }
    }
  }
`)

type Props = {
  orderItem: FragmentType<typeof canteenOrderedItemCardOrderItemFragment>
  userData: FragmentType<typeof canteenOrderedItemCardUserDataFragment>
  pendingCancellation?: boolean
  cancelOrder: (ccOrderItemId: number) => void
  showItemDetails: () => void
}

const CCOrderedItemCard: React.FC<Props> = ({
  orderItem: orderItemProps,
  userData: userDataProp,
  pendingCancellation,
  cancelOrder,
  showItemDetails,
}) => {
  const orderItem = useFragment(canteenOrderedItemCardOrderItemFragment, orderItemProps)
  const userData = useFragment(canteenOrderedItemCardUserDataFragment, userDataProp)
  const hidePrices = Boolean(userData.account?.userInterfaceSettings.hideCCPrices)

  return (
    <ItemCard
      pendingRemoval={pendingCancellation}
      vendorAvatar={orderItem.vendor.logo}
      vendorName={orderItem.vendor.companyName}
      imageTitle={orderItem.name}
      imageUrl={orderItem.liveMenuItem.coreLiveItem?.image || ''}
    >
      <CardHeader title={orderItem.name} />
      <CardContent>
        <Stack marginBottom={1} spacing={1} direction="column" flexWrap="wrap">
          <Chip
            sx={{ width: 'fit-content' }}
            icon={<AccessTime />}
            size="small"
            label={moment(orderItem.ccSupplierOrder?.arrivalTime).format('HH:mm')}
          />
          <Chip
            sx={{ width: 'fit-content' }}
            size="small"
            icon={<Place />}
            label={orderItem.accountLocation.label}
          />
        </Stack>
        {!hidePrices ? (
          <Typography textAlign="right">
            {orderItem.qty} x {numberFormat(orderItem.unitPrice)}
          </Typography>
        ) : null}
      </CardContent>
      <CardActions>
        <Stack width="100%" direction="row-reverse" alignItems="center" spacing={0.5}>
          <Button
            onClick={() => {
              cancelOrder(orderItem.id)
            }}
            disabled={!orderItem.canCancel}
            size="small"
            variant="contained"
            color="error"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              showItemDetails()
            }}
            size="small"
            variant="contained"
            color="primary"
          >
            See more
          </Button>
        </Stack>
      </CardActions>
    </ItemCard>
  )
}

export default CCOrderedItemCard
